import React, { useRef } from 'react';
import { isBuilderSubscriptionPayload } from '../SubscriptionSummary/builderSubscriptionMeta';
import SubscriptionSlotsPatternTable from '../SubscriptionSlotsPattern/SubscriptionSlotsPatternTable';
import {
  formatReceiptCurrency,
  RECEIPT_DELIVERY_CHARGE_PER_DAY,
} from './subscriptionReceiptUtils';
import './SubscriptionReceiptModal.css';

/**
 * Meal plan subscription receipt modal (same UI as post-create receipt).
 *
 * @param {{
 *   invoiceData: object,
 *   open?: boolean,
 *   onClose: () => void,
 *   title?: string,
 * }} props
 */
export default function SubscriptionReceiptModal({
  invoiceData,
  open = true,
  onClose,
  title = 'Request Received!',
}) {
  const receiptRef = useRef(null);

  if (!open || !invoiceData) return null;

  const printReceipt = () => {
    const node = receiptRef.current;
    if (!node) {
      window.print();
      return;
    }

    const headStyles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((el) => el.outerHTML)
      .join('\n');

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      cleanup();
      window.print();
      return;
    }

    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Subscription Receipt</title>${headStyles}</head>`
      + `<body class="mp-receipt-print-body"><div class="mp-product-modal-overlay">${node.outerHTML}</div></body></html>`,
    );
    doc.close();

    const triggerPrint = () => {
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        window.print();
        return;
      }
      win.addEventListener?.('afterprint', cleanup);
      window.setTimeout(cleanup, 60000);
      try {
        win.focus();
        win.print();
      } catch {
        cleanup();
        window.print();
      }
    };

    if (iframe.contentWindow?.document.readyState === 'complete') {
      window.setTimeout(triggerPrint, 400);
    } else {
      iframe.onload = () => window.setTimeout(triggerPrint, 400);
    }
  };

  const hideSchedule = isBuilderSubscriptionPayload(invoiceData);
  const lineItems = Array.isArray(invoiceData.lineItems) ? invoiceData.lineItems : [];
  const slotPatternRows = Array.isArray(invoiceData.slotPatternRows)
    ? invoiceData.slotPatternRows
    : [];
  const specialtyLabel = invoiceData.specialtyLabel || '—';
  const durationPlan = invoiceData.planLabel && String(invoiceData.planLabel).trim()
    ? String(invoiceData.planLabel).trim()
    : null;

  return (
    <div
      className="mp-product-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mp-invoice-modal" ref={receiptRef}>
        <div className="mp-product-modal__header">
          <div>
            <h3>{title}</h3>
            <p>Subscription Receipt · Reference ID: {invoiceData.referenceId}</p>
            {invoiceData.lookupCode ? <p>Lookup Code: {invoiceData.lookupCode}</p> : null}
          </div>
          <button
            type="button"
            className="mp-product-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="mp-invoice-modal__body">
          <div className="mp-invoice-paper-head">
            <h4>DIGIRASA</h4>
            <p>Miyapur, Hyderabad</p>
            <p>Meal Subscription Receipt</p>
          </div>
          <div className="mp-invoice-meta">
            <p><strong>Generated:</strong> {invoiceData.generatedAt}</p>
            <p>
              <strong>Customer:</strong> {invoiceData.customerName}
              {invoiceData.customerPhone ? ` (${invoiceData.customerPhone})` : ''}
            </p>
            <p>
              <strong>Area:</strong> {invoiceData.customerArea || '—'}
              {invoiceData.customerArea ? ', Hyderabad' : ''}
            </p>
            <p>
              <strong>Specialty:</strong> {specialtyLabel}
            </p>
            {invoiceData.mealTypeLabel ? (
              <p><strong>Meal type:</strong> {invoiceData.mealTypeLabel}</p>
            ) : null}
            {durationPlan ? (
              <p><strong>Plan:</strong> {durationPlan}</p>
            ) : null}
            {!hideSchedule && invoiceData.scheduleLabel ? (
              <p><strong>Schedule:</strong> {invoiceData.scheduleLabel}</p>
            ) : null}
            <p><strong>Date Range:</strong> {invoiceData.dateRange || '—'}</p>
          </div>

          {slotPatternRows.length ? (
            <div className="mp-invoice-slots">
              <p><strong>Meal slots &amp; pattern</strong></p>
              <SubscriptionSlotsPatternTable
                rows={slotPatternRows}
                compact
                className="slot-pattern-wrap--receipt"
              />
            </div>
          ) : invoiceData.mealLabel ? (
            <div className="mp-invoice-slots">
              <p><strong>Meal slots:</strong> {invoiceData.mealLabel}</p>
            </div>
          ) : null}

          <div className="mp-invoice-table">
            <div className="mp-invoice-row mp-invoice-row--head">
              <span>Item</span>
              <span>Qty</span>
              <span>Rate</span>
              <span>Total</span>
            </div>
            {lineItems.length ? (
              lineItems.map((item, idx) => (
                <div key={`${item.name}-${idx}`} className="mp-invoice-row">
                  <span>
                    {item.name}
                    {item.includedInPlan ? ' (1 included)' : ''}
                  </span>
                  <span>{item.quantity}</span>
                  <span>{formatReceiptCurrency(item.price)}</span>
                  <span>{formatReceiptCurrency(item.lineTotal)}</span>
                </div>
              ))
            ) : (
              <div className="mp-invoice-row">
                <span>Meal plan subscription</span>
                <span>1</span>
                <span>{formatReceiptCurrency(invoiceData.grandTotal)}</span>
                <span>{formatReceiptCurrency(invoiceData.grandTotal)}</span>
              </div>
            )}
          </div>

          <div className="mp-invoice-summary">
            {invoiceData.actualSubtotal != null ? (
              <>
                <p>
                  <span>Actual price</span>
                  <strong>{formatReceiptCurrency(invoiceData.actualSubtotal)}</strong>
                </p>
                <p>
                  <span>
                    {invoiceData.discountPercent > 0
                      ? `Discount (${invoiceData.discountPercent}%)`
                      : 'Discount'}
                  </span>
                  <strong>{formatReceiptCurrency(invoiceData.discountAmount || 0)}</strong>
                </p>
              </>
            ) : null}
            <p>
              <span>Plan subtotal</span>
              <strong>{formatReceiptCurrency(invoiceData.subtotal)}</strong>
            </p>
            <p>
              <span>Packaging</span>
              <strong>{formatReceiptCurrency(invoiceData.packagingCharges || 0)}</strong>
            </p>
            <p>
              <span>GST ({invoiceData.gstPercent || 5}%)</span>
              <strong>{formatReceiptCurrency(invoiceData.gstAmount)}</strong>
            </p>
            <p>
              <span>
                {invoiceData.isFreeDeliveryPlan
                  ? 'Delivery (free with plan)'
                  : invoiceData.distanceKm != null
                    ? `Delivery (${Number(invoiceData.distanceKm).toFixed(1)} km)`
                    : 'Delivery'}
              </span>
              <strong>{formatReceiptCurrency(invoiceData.deliveryCharges)}</strong>
            </p>
            {Number(invoiceData.specialDiscountAmount) > 0.009 ? (
              <>
                {invoiceData.originalGrandTotal != null ? (
                  <p>
                    <span>Original total</span>
                    <strong>{formatReceiptCurrency(invoiceData.originalGrandTotal)}</strong>
                  </p>
                ) : null}
                <p>
                  <span>Special discount</span>
                  <strong>{formatReceiptCurrency(invoiceData.specialDiscountAmount)}</strong>
                </p>
              </>
            ) : null}
            <p className="mp-invoice-summary__grand">
              <span>Total amount</span>
              <strong>{formatReceiptCurrency(invoiceData.grandTotal)}</strong>
            </p>
            <small>
              {invoiceData.isFreeDeliveryPlan
                ? 'Delivery included for selected plan.'
                : invoiceData.distanceKm != null
                  ? `Delivery based on ${Number(invoiceData.distanceKm).toFixed(1)} km from kitchen × ${invoiceData.deliveryDayCount} day(s).`
                  : `Delivery estimated at ${formatReceiptCurrency(RECEIPT_DELIVERY_CHARGE_PER_DAY)} per delivery day × ${invoiceData.deliveryDayCount} day(s).`}
            </small>
          </div>
          <div className="mp-invoice-paper-foot">
            <p>Thank you for choosing Digirasa!</p>
            <p>customercare@digirasa.com | +91 81432 31857</p>
          </div>
        </div>

        <div className="mp-customer-modal__footer">
          <button type="button" className="mp-cancel-edit" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn-primary" onClick={printReceipt}>
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
}
